/**
 * Client HTTP générique pour l'API Nestor — Phase 4, incrément 4.3.
 *
 * Wrapper léger autour de `fetch` qui :
 *
 *   - **inclut systématiquement les cookies** (`credentials: 'include'`)
 *     pour transporter le cookie httpOnly `nestor_session` posé par
 *     `POST /auth/login` (ADR 0008).
 *   - **parse la réponse JSON** automatiquement quand l'API retourne
 *     du JSON.
 *   - **normalise les erreurs** dans une classe `ApiError` typée que
 *     les call sites peuvent distinguer du `TypeError` réseau.
 *   - **baseURL configurable** via `VITE_API_URL` env (vite expose
 *     `import.meta.env.VITE_*`). Default `http://127.0.0.1:3000` en
 *     dev.
 *
 * Pas de retry, pas de cache : on garde le wrapper minimal. La queue
 * offline (Phase 3.3) viendra l'envelopper plus tard.
 */

/**
 * URL base de l'API. Configurable au build via `VITE_API_URL`.
 * Default dev = Fastify sur 127.0.0.1:3000.
 *
 * En prod (Fly.io, etc.), passer `VITE_API_URL=https://api.nestor.app`
 * au moment du `vite build`. Vite inline cette constante dans le
 * bundle — pas de runtime resolution.
 */
export const API_BASE_URL: string =
  (import.meta.env['VITE_API_URL'] as string | undefined) ?? 'http://127.0.0.1:3000';

/**
 * Erreur API normalisée. Tous les call sites peuvent matcher
 * `err instanceof ApiError` pour distinguer une erreur HTTP d'un
 * problème réseau (TypeError côté fetch) ou autre.
 */
export class ApiError extends Error {
  readonly status: number;
  readonly code: string | null;
  readonly details: unknown;

  constructor(status: number, code: string | null, details: unknown, message?: string) {
    super(message ?? code ?? `HTTP ${status.toString()}`);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
    this.details = details;
  }
}

export interface ApiRequestOptions {
  /** Méthode HTTP. Default GET. */
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  /** Body à sérialiser en JSON. Si présent, ajoute `Content-Type`. */
  body?: unknown;
  /** Headers additionnels (mergés avec ceux par défaut). */
  headers?: Record<string, string>;
  /** AbortSignal pour annuler la requête. */
  signal?: AbortSignal;
}

/**
 * Effectue une requête vers l'API. Sérialise le body en JSON,
 * parse la réponse en JSON si `Content-Type: application/json`, et
 * throw `ApiError` sur status >= 400.
 *
 * Le path est relatif à `API_BASE_URL` ; **doit commencer par `/`**
 * (sinon URL absolue / relative ambiguë).
 */
export async function apiRequest<T = unknown>(
  path: string,
  options: ApiRequestOptions = {},
): Promise<T> {
  if (!path.startsWith('/')) {
    throw new Error(`apiRequest: path doit commencer par '/', reçu '${path}'`);
  }
  const url = `${API_BASE_URL}${path}`;
  const headers: Record<string, string> = {
    Accept: 'application/json',
    ...options.headers,
  };
  let body: BodyInit | undefined;
  if (options.body !== undefined) {
    headers['Content-Type'] = 'application/json';
    body = JSON.stringify(options.body);
  }

  const init: RequestInit = {
    method: options.method ?? 'GET',
    credentials: 'include',
    headers,
    ...(body !== undefined ? { body } : {}),
    ...(options.signal ? { signal: options.signal } : {}),
  };

  const response = await fetch(url, init);

  // 204 No Content : pas de body à parser. Retourne undefined-as-T.
  if (response.status === 204) {
    return undefined as T;
  }

  const contentType = response.headers.get('content-type') ?? '';
  let data: unknown = null;
  if (contentType.includes('application/json')) {
    try {
      data = (await response.json()) as unknown;
    } catch {
      // JSON malformé : on garde data = null et on throw plus bas si !ok.
    }
  } else if (response.body) {
    // Pour les futurs endpoints qui retournent autre chose que du JSON
    // (ex. binary stream d'une photo) — on laisse le caller gérer.
    data = await response.text();
  }

  if (!response.ok) {
    const code = isErrorBody(data) ? data.error : null;
    const details = isErrorBody(data) ? data.details : data;
    throw new ApiError(response.status, code, details);
  }

  return data as T;
}

interface ErrorBody {
  error: string;
  details?: unknown;
}

function isErrorBody(data: unknown): data is ErrorBody {
  return (
    typeof data === 'object' &&
    data !== null &&
    'error' in data &&
    typeof (data as { error: unknown }).error === 'string'
  );
}
